import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { FiDollarSign, FiCreditCard, FiSmartphone, FiBriefcase, FiUsers, FiLayers, FiX, FiPlus, FiCheck } from 'react-icons/fi';
import { colors, borderRadius, typography, shadows } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';

const METHODS = [
  { key: 'cash',   label: 'CASH',   icon: FiDollarSign,  accent: '#1B8E5A', kind: 'cash' },
  { key: 'card',   label: 'CARD',   icon: FiCreditCard,  accent: '#3B6CF1', kind: 'bank' },
  { key: 'wallet', label: 'WALLET', icon: FiSmartphone,  accent: '#7C3AED', kind: 'bank' },
  { key: 'bank',   label: 'BANK',   icon: FiBriefcase,   accent: '#0891B2', kind: 'bank' },
  { key: 'credit', label: 'CREDIT', icon: FiUsers,       accent: '#B45309', kind: 'credit' },
];

/**
 * Inline POS payment bar. Sits below the cart — no modal, no overlay.
 *
 * Behavior:
 *   - Default method = Cash. Click chip → switch method instantly.
 *   - Cash: tendered field + quick-amount chips + live Change.
 *   - Card/Wallet/Bank: small account dropdown + optional reference.
 *   - Credit: requires a non-walk-in customer (disabled chip otherwise).
 *   - Split: expands a small editor with multiple tender rows.
 *   - Press SPACE or ENTER to fire the current method.
 */
const PaymentBar = ({
  totalAmount,
  paymentAccounts = [],
  cashTendered,
  onCashTenderedChange,
  isWalkInCustomer = true,
  onComplete,
  disabled = false,
  busy = false,
  customerId,
  customerName,
  // POS bank card discounts (module pos_bank_discounts). All optional — when
  // `bankOffers` is empty (module off / no offers) nothing below renders.
  bankOffers = [],
  bankDiscount = null,
  bankDiscountNote = '',
  onApplyBankDiscount,
  onClearBankDiscount,
}) => {
  const { formatCurrency } = useCurrency();
  const [method, setMethod] = useState('cash');
  const [bankAccountId, setBankAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitLines, setSplitLines] = useState([]);
  const [error, setError] = useState('');
  const cashRef = useRef(null);
  const [offerId, setOfferId] = useState('');
  const [cardFirst6, setCardFirst6] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [applying, setApplying] = useState(false);
  const showBankOffers = bankOffers.length > 0 || !!bankDiscount;
  // Leaving the card method (or opening split) drops an applied bank discount.
  const dropBankDiscount = () => { if (bankDiscount) onClearBankDiscount?.(); };
  // After a sale (bill back to zero) start the offer fields fresh. A bill edit
  // keeps them, so the cashier just presses Apply again.
  useEffect(() => { if (!(totalAmount > 0)) { setOfferId(''); setCardFirst6(''); setCardLast4(''); } }, [totalAmount]);
  const applyOffer = async () => {
    const offer = bankOffers.find(o => o.id === offerId);
    if (!offer) { setError('Select a bank offer'); return; }
    if (!/^\d{6}$/.test(cardFirst6)) { setError('Enter the first 6 digits of the card'); return; }
    if (!/^\d{4}$/.test(cardLast4)) { setError('Enter the last 4 digits of the card'); return; }
    setError(''); setApplying(true);
    try {
      const res = await onApplyBankDiscount?.(offer, cardFirst6, cardLast4);
      if (res && res.ok === false) setError(res.error || 'Offer could not be applied');
    } finally { setApplying(false); }
  };

  const cashAccounts = useMemo(() => paymentAccounts.filter(a => (a.code || '').startsWith('11101')), [paymentAccounts]);
  const bankAccounts = useMemo(() => paymentAccounts.filter(a => (a.code || '').startsWith('11102')), [paymentAccounts]);

  // Auto-pick a bank account when switching to a bank-kind method
  useEffect(() => {
    const m = METHODS.find(x => x.key === method);
    if (m?.kind === 'bank' && !bankAccountId && bankAccounts.length > 0) {
      setBankAccountId(bankAccounts[0].id);
    }
  }, [method, bankAccountId, bankAccounts]);

  // Reset transient state when total changes (after a sale).
  useEffect(() => { setReference(''); setError(''); setSplitOpen(false); }, [totalAmount]);

  // Auto-focus cash field when switching to cash method.
  useEffect(() => { if (method === 'cash') setTimeout(() => cashRef.current?.focus?.(), 30); }, [method]);

  const cashTenderedNum = Number(cashTendered) || 0;
  const cashChange = Math.max(0, cashTenderedNum - totalAmount);

  const quickAmounts = useMemo(() => {
    if (!totalAmount) return [];
    const bases = [10, 50, 100, 500, 1000, 5000];
    const out = [totalAmount];
    for (const b of bases) {
      const r = Math.ceil(totalAmount / b) * b;
      if (r > totalAmount && !out.includes(r)) {
        out.push(r);
        if (out.length >= 4) break;
      }
    }
    return out;
  }, [totalAmount]);

  const splitSum = splitLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const splitRemaining = Math.max(0, totalAmount - splitSum);

  const getAcc = (id) => paymentAccounts.find(a => a.id === id);

  const fireCash = () => {
    // When totalAmount is 0 (e.g. a refund-only exchange), tendered=0 is fine.
    // We only reject when the cashier under-tendered against a positive total.
    if (totalAmount > 0 && cashTenderedNum < totalAmount) {
      setError('Tendered amount must be at least the total');
      return;
    }
    const acc = cashAccounts[0];
    onComplete?.({
      paymentMethod: 'cash',
      payments: [{
        method: 'cash',
        accountId: acc?.id || '',
        accountCode: acc?.code || '',
        accountName: acc?.name || 'Cash in Hand',
        amount: totalAmount,
        reference: ''
      }],
      cashTendered: cashTenderedNum,
      changeGiven: cashChange
    });
  };

  const fireBank = () => {
    if (!bankAccountId) { setError('Select a bank account'); return; }
    const acc = getAcc(bankAccountId);
    onComplete?.({
      paymentMethod: method,
      payments: [{
        method,
        accountId: bankAccountId,
        accountCode: acc?.code || '',
        accountName: acc?.name || method,
        amount: totalAmount,
        reference
      }]
    });
  };

  const fireCredit = () => {
    if (isWalkInCustomer) { setError('Credit requires a registered customer'); return; }
    onComplete?.({
      paymentMethod: 'credit',
      customerId,
      customerName,
      payments: [{ method: 'credit', accountId: '', accountCode: '', accountName: '', amount: totalAmount, reference: '' }]
    });
  };

  const fireSplit = () => {
    if (splitLines.length === 0) { setError('Add at least one split line'); return; }
    if (splitSum > totalAmount + 0.01) { setError(`Over by ${formatCurrency(splitSum - totalAmount)}`); return; }
    const remaining = Math.round((totalAmount - splitSum) * 100) / 100;
    let payments = splitLines.map(l => {
      const acc = getAcc(l.accountId);
      return {
        method: l.method,
        accountId: l.accountId || '',
        accountCode: acc?.code || '',
        accountName: acc?.name || l.method,
        amount: Number(l.amount) || 0,
        reference: l.reference || ''
      };
    });
    if (remaining > 0.01) {
      if (isWalkInCustomer) {
        setError(`Remaining ${formatCurrency(remaining)} cannot go on credit for Walk-in`); return;
      }
      payments.push({ method: 'credit', accountId: '', accountCode: '', accountName: '', amount: remaining, reference: '' });
    }
    onComplete?.({
      paymentMethod: 'split',
      customerId, customerName,
      payments
    });
  };

  const fire = () => {
    // totalAmount can legitimately be 0 in a refund-only exchange — the parent
    // controls enablement, so we no longer block here on amount alone.
    if (disabled || busy) return;
    setError('');
    if (splitOpen) return fireSplit();
    if (method === 'cash') return fireCash();
    if (method === 'credit') return fireCredit();
    return fireBank();
  };

  // Keyboard: Space/Enter triggers current method when bar is the focus context.
  useEffect(() => {
    const handler = (e) => {
      if (disabled || busy) return;
      // Don't hijack when typing in normal inputs other than our own.
      const tag = e.target?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (e.key === ' ' && !isInput) { e.preventDefault(); fire(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [method, splitOpen, splitLines, totalAmount, cashTendered, bankAccountId, reference, disabled, busy, isWalkInCustomer]);

  const addSplit = () => setSplitLines([...splitLines, {
    method: 'cash',
    accountId: cashAccounts[0]?.id || '',
    amount: splitRemaining || '',
    reference: ''
  }]);
  const updSplit = (i, patch) => setSplitLines(prev => {
    const next = [...prev]; next[i] = { ...next[i], ...patch };
    if (patch.method) {
      if (patch.method === 'cash') next[i].accountId = cashAccounts[0]?.id || '';
      else if (patch.method === 'credit') next[i].accountId = '';
      else next[i].accountId = bankAccounts[0]?.id || '';
    }
    return next;
  });
  const rmSplit = (i) => setSplitLines(prev => prev.filter((_, idx) => idx !== i));

  const m = METHODS.find(x => x.key === method);

  return (
    <Wrap>
      <Row1>
        <MethodChips>
          {METHODS.map(opt => {
            const dis = opt.key === 'credit' && isWalkInCustomer;
            return (
              <Chip key={opt.key}
                $active={method === opt.key && !splitOpen}
                $accent={opt.accent}
                $disabled={dis}
                onClick={() => { if (dis) return; if (opt.key !== 'card') dropBankDiscount(); setSplitOpen(false); setMethod(opt.key); setError(''); }}
                title={dis ? 'Select a customer first' : ''}
              >
                <opt.icon size={16} />
                <span>{opt.label}</span>
              </Chip>
            );
          })}
          <Chip $active={splitOpen} $accent="#374151"
            onClick={() => {
              if (!splitOpen) dropBankDiscount();
              setSplitOpen(s => !s);
              setError('');
              if (!splitOpen && splitLines.length === 0) {
                setSplitLines([{ method: 'cash', accountId: cashAccounts[0]?.id || '', amount: totalAmount, reference: '' }]);
              }
            }}>
            <FiLayers size={16} /><span>SPLIT</span>
          </Chip>
        </MethodChips>

        <PayBtn
          $accent={splitOpen ? '#374151' : (m?.accent || '#1B8E5A')}
          disabled={disabled || busy}
          onClick={fire}
        >
          {busy ? 'SAVING…' : (
            <>
              <FiCheck size={20} />
              <span>PAY {formatCurrency(totalAmount)}</span>
            </>
          )}
        </PayBtn>
      </Row1>

      {!splitOpen && method === 'cash' && (
        <Row2>
          <Field>
            <Lbl>Tendered</Lbl>
            <BigInput
              ref={cashRef}
              type="number"
              value={cashTendered ?? ''}
              onChange={e => onCashTenderedChange?.(e.target.value)}
              placeholder="0"
              inputMode="decimal"
            />
          </Field>
          <QuickRow>
            {quickAmounts.map((a, i) => (
              <QuickBtn key={i} onClick={() => onCashTenderedChange?.(String(a))}>
                {i === 0 ? 'Exact' : formatCurrency(a)}
              </QuickBtn>
            ))}
          </QuickRow>
          <ChangeBox $shown={cashTenderedNum >= totalAmount && totalAmount > 0}>
            <span>Change</span>
            <strong>{formatCurrency(cashChange)}</strong>
          </ChangeBox>
        </Row2>
      )}

      {!splitOpen && (method === 'card' || method === 'wallet' || method === 'bank') && (
        <Row2>
          <Field>
            <Lbl>{method === 'card' ? 'Bank for Card Settlement' : method === 'wallet' ? 'Wallet Account' : 'Bank Account'}</Lbl>
            <Sel value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
              <option value="">-- Select --</option>
              {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
            </Sel>
          </Field>
          <Field $grow>
            <Lbl>Reference (optional)</Lbl>
            <Inp value={reference} onChange={e => setReference(e.target.value)} placeholder={method === 'card' ? 'Approval code' : 'Txn ID'} />
          </Field>
        </Row2>
      )}

      {!splitOpen && method === 'card' && showBankOffers && (
        <BankRow>
          {bankDiscount ? (
            <BankApplied>
              <FiCheck size={14} />
              <span>
                <b>{bankDiscount.label} {bankDiscount.percent}%</b> — card {bankDiscount.cardMask}
                {' · '}Discount <b>-{formatCurrency(bankDiscount.amount || 0)}</b>
              </span>
              <BankRemove type="button" onClick={() => onClearBankDiscount?.()} title="Remove bank discount"><FiX size={13} /> Remove</BankRemove>
            </BankApplied>
          ) : (
            <>
              <Field $grow>
                <Lbl>Bank Card Offer</Lbl>
                <Sel value={offerId} onChange={e => { setOfferId(e.target.value); setError(''); }} disabled={!!bankDiscountNote}>
                  <option value="">-- No bank offer --</option>
                  {bankOffers.map(o => (
                    <option key={o.id} value={o.id}>
                      {`${o.bankName} ${o.cardType || ''}`.trim()} — {o.discountPercent}%{Number(o.capPerBill) > 0 ? ` (max ${o.capPerBill})` : ''}
                    </option>
                  ))}
                </Sel>
              </Field>
              <Field>
                <Lbl>Card first 6</Lbl>
                <Inp value={cardFirst6} maxLength={6} inputMode="numeric" placeholder="123456" style={{ width: 90 }}
                  onChange={e => setCardFirst6(e.target.value.replace(/\D/g, '').slice(0, 6))} disabled={!!bankDiscountNote} />
              </Field>
              <Field>
                <Lbl>Last 4</Lbl>
                <Inp value={cardLast4} maxLength={4} inputMode="numeric" placeholder="1234" style={{ width: 70 }}
                  onChange={e => setCardLast4(e.target.value.replace(/\D/g, '').slice(0, 4))} disabled={!!bankDiscountNote} />
              </Field>
              <ApplyBtn type="button" onClick={applyOffer} disabled={applying || !offerId || !!bankDiscountNote || disabled || busy}>
                {applying ? 'Checking…' : 'Apply'}
              </ApplyBtn>
              {bankDiscountNote && <BankNote>{bankDiscountNote}</BankNote>}
            </>
          )}
        </BankRow>
      )}

      {!splitOpen && method === 'credit' && (
        <Row2>
          <CreditNote>
            {isWalkInCustomer
              ? 'Select a registered customer to use credit.'
              : `On credit to: ${customerName || 'customer'} — full amount will go to A/R.`}
          </CreditNote>
        </Row2>
      )}

      {splitOpen && (
        <SplitWrap>
          <SplitHead>
            <span>Total: <b>{formatCurrency(totalAmount)}</b></span>
            <span>Paid: <b>{formatCurrency(splitSum)}</b></span>
            <span style={{ color: splitRemaining > 0 ? colors.error.main : colors.success.main }}>
              Remaining: <b>{formatCurrency(splitRemaining)}</b>
            </span>
            <AddBtn onClick={addSplit}><FiPlus size={14} /> Add tender</AddBtn>
          </SplitHead>
          {splitLines.map((l, i) => (
            <SplitRow key={i}>
              <Sel value={l.method} onChange={e => updSplit(i, { method: e.target.value })}>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="wallet">Wallet</option>
                <option value="bank">Bank</option>
                <option value="credit" disabled={isWalkInCustomer}>Credit</option>
              </Sel>
              {l.method !== 'credit' && (
                <Sel value={l.accountId} onChange={e => updSplit(i, { accountId: e.target.value })}>
                  <option value="">-- Account --</option>
                  {(l.method === 'cash' ? cashAccounts : bankAccounts).map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </Sel>
              )}
              <Inp
                type="number"
                value={l.amount}
                onChange={e => updSplit(i, { amount: e.target.value })}
                placeholder="0"
              />
              <Inp
                value={l.reference}
                onChange={e => updSplit(i, { reference: e.target.value })}
                placeholder="Ref"
              />
              <RmBtn onClick={() => rmSplit(i)} disabled={splitLines.length <= 1}><FiX size={14} /></RmBtn>
            </SplitRow>
          ))}
        </SplitWrap>
      )}

      {error && <ErrLine>{error}</ErrLine>}
    </Wrap>
  );
};

// ─── Styled ───────────────────────────────────────────────────────────────

// Mobile Pay-step polish. Desktop layout is untouched — every rule below is
// guarded by this breakpoint. On mobile the bar becomes a clean vertical stack:
// payment-method grid → tendered + quick amounts → full-width PAY at the bottom.
const PB_MOBILE = '@media (max-width: 820px)';

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 8px 12px;
  background: ${colors.neutral[50]};
  border-top: 2px solid ${colors.border.light};
  ${PB_MOBILE} { gap: 12px; padding: 14px 14px 18px; }
`;
const Row1 = styled.div`
  display: flex; align-items: stretch; gap: 8px;
  ${PB_MOBILE} { display: contents; }  /* let chips + PAY reorder within Wrap */
`;
const MethodChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; flex: 1;
  ${PB_MOBILE} {
    order: 1; width: 100%; flex: none;
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
  }
`;
const Chip = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 12px;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
  border: 2px solid ${({ $active, $accent }) => $active ? $accent : 'transparent'};
  background: ${({ $active, $accent }) => $active ? `${$accent}15` : 'white'};
  color: ${({ $active, $accent }) => $active ? $accent : colors.text.primary};
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.4 : 1};
  transition: all 0.1s;
  &:hover { background: ${({ $accent, $disabled }) => $disabled ? 'white' : `${$accent}10`}; }
  ${PB_MOBILE} {
    justify-content: center; padding: 14px 6px; font-size: 13px;
    border: 2px solid ${({ $active, $accent }) => $active ? $accent : colors.border.light};
  }
`;
const PayBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-width: 220px;
  padding: 0 22px;
  background: ${({ $accent }) => $accent};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: ${shadows.sm};
  &:hover:not(:disabled) { filter: brightness(0.92); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  ${PB_MOBILE} {
    order: 3; width: 100%; min-width: 0;
    min-height: 58px; padding: 14px; font-size: 19px; margin-top: 2px;
  }
`;
const Row2 = styled.div`
  display: flex; align-items: end; gap: 10px;
  padding-top: 4px;
  ${PB_MOBILE} {
    order: 2; flex-direction: column; align-items: stretch; width: 100%; gap: 12px;
  }
`;
const Field = styled.div`
  display: flex; flex-direction: column; gap: 3px;
  flex: ${({ $grow }) => $grow ? 1 : 0};
  ${PB_MOBILE} { width: 100%; flex: none; }
`;
const Lbl = styled.label`
  font-size: 10px; font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary}; letter-spacing: 0.5px;
`;
const BigInput = styled.input`
  width: 180px;
  padding: 8px 10px;
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  border: 2px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }
  ${PB_MOBILE} { width: 100%; font-size: 26px; padding: 12px 14px; text-align: right; }
`;
const Inp = styled.input`
  padding: 8px 10px;
  font-size: ${typography.fontSize.sm};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }
`;
const Sel = styled.select`
  padding: 8px 10px;
  font-size: ${typography.fontSize.sm};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  background: white;
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }
`;
const QuickRow = styled.div`
  display: flex; gap: 4px; flex-wrap: wrap;
  ${PB_MOBILE} { gap: 8px; }
`;
const QuickBtn = styled.button`
  padding: 6px 12px;
  border-radius: ${borderRadius.sm};
  border: 1px solid ${colors.border.light};
  background: white;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  &:hover { background: ${colors.neutral[100]}; }
  ${PB_MOBILE} { flex: 1; min-width: 64px; padding: 11px 8px; font-size: 13px; }
`;
const ChangeBox = styled.div`
  display: ${({ $shown }) => $shown ? 'flex' : 'none'};
  align-items: center; gap: 8px;
  padding: 6px 12px;
  background: #E8F5E9; color: #1B8E5A;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  & strong { font-size: ${typography.fontSize.lg}; }
`;
const CreditNote = styled.div`
  padding: 8px 12px;
  background: #FFF8E1; color: #B45309;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  flex: 1;
`;
const SplitWrap = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding: 6px 8px;
  background: white;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
`;
const SplitHead = styled.div`
  display: flex; align-items: center; gap: 16px;
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;
const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 120px 1fr 30px;
  gap: 6px;
  align-items: center;
`;
const AddBtn = styled.button`
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  background: ${colors.primary.main}; color: white;
  border: none; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  &:hover { filter: brightness(0.9); }
`;
const RmBtn = styled.button`
  width: 26px; height: 26px;
  display: inline-flex; align-items: center; justify-content: center;
  background: white; border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  cursor: pointer; color: ${colors.text.secondary};
  &:hover:not(:disabled) { color: ${colors.error.main}; border-color: ${colors.error.main}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const BankRow = styled.div`
  display: flex; align-items: end; gap: 8px; flex-wrap: wrap;
  padding: 6px 8px;
  background: #EEF3FF;
  border: 1px dashed #3B6CF1;
  border-radius: ${borderRadius.sm};
  ${PB_MOBILE} { flex-direction: column; align-items: stretch; }
`;
const BankApplied = styled.div`
  display: flex; align-items: center; gap: 8px; flex: 1;
  color: #1E40AF; font-size: ${typography.fontSize.sm};
  & b { font-weight: ${typography.fontWeight.bold}; }
`;
const BankRemove = styled.button`
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  background: white; color: ${colors.error.main};
  border: 1px solid ${colors.error.main}; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs}; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
`;
const ApplyBtn = styled.button`
  padding: 8px 16px;
  background: #3B6CF1; color: white;
  border: none; border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.bold};
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const BankNote = styled.div`
  font-size: ${typography.fontSize.xs}; color: #B45309; font-weight: ${typography.fontWeight.semibold};
  align-self: center;
`;
const ErrLine = styled.div`
  padding: 4px 10px;
  background: #FFEBEE; color: ${colors.error.main};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
`;

export default PaymentBar;
